import { signal } from '@angular/core';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { SORTABLE_GRID_LABELS } from './localization/sortable-grid.labels';

export interface SortableGridKeyboardOptions<T> {
  items: () => T[];
  name: (item: T) => string;
  reorder: (items: T[]) => void;
  announce: (message: string) => void;
  /** Called after the active index changed; the host focuses the item once it is rendered */
  focusItem: (index: number) => void;
}

/**
 * Keyboard alternative for a drag-and-drop sorted grid (WCAG 2.1.1, 2.5.1). The grid is one tab
 * stop (roving tabindex): arrow keys move the focus, Space/Enter picks an item up and drops it,
 * arrow keys move a picked item, Escape restores its original position.
 */
export class SortableGridKeyboard<T> {
  readonly activeIndex = signal(0);
  readonly pickedIndex = signal<number | null>(null);
  private originalIndex = 0;

  constructor(private readonly options: SortableGridKeyboardOptions<T>) {}

  tabIndexFor(index: number): number {
    const last = this.options.items().length - 1;
    return index === Math.min(this.activeIndex(), last) ? 0 : -1;
  }

  onFocus(index: number): void {
    this.activeIndex.set(index);
  }

  onKeydown(event: KeyboardEvent): void {
    const items = this.options.items();
    const total = items.length;
    if (total === 0) return;

    const current = Math.min(this.activeIndex(), total - 1);
    let target: number;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        target = Math.min(current + 1, total - 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        target = Math.max(current - 1, 0);
        break;
      case 'Home':
        target = 0;
        break;
      case 'End':
        target = total - 1;
        break;
      case ' ':
      case 'Enter':
        event.preventDefault();
        this.togglePicked(items, current);
        return;
      case 'Escape':
        if (this.pickedIndex() === null) return;
        event.preventDefault();
        this.cancel(items, current);
        return;
      default:
        return;
    }
    event.preventDefault();

    if (this.pickedIndex() !== null && target !== current) {
      const reordered = [...items];
      moveItemInArray(reordered, current, target);
      this.options.reorder(reordered);
      this.pickedIndex.set(target);
      this.options.announce(
        SORTABLE_GRID_LABELS.moved(
          this.options.name(reordered[target]),
          target + 1,
          total
        )
      );
    }
    this.activeIndex.set(target);
    this.options.focusItem(target);
  }

  private togglePicked(items: T[], index: number): void {
    const name = this.options.name(items[index]);
    if (this.pickedIndex() === null) {
      this.pickedIndex.set(index);
      this.originalIndex = index;
      this.options.announce(
        SORTABLE_GRID_LABELS.pickedUp(name, index + 1, items.length)
      );
    } else {
      this.pickedIndex.set(null);
      this.options.announce(
        SORTABLE_GRID_LABELS.dropped(name, index + 1, items.length)
      );
    }
  }

  private cancel(items: T[], index: number): void {
    const reordered = [...items];
    moveItemInArray(reordered, index, this.originalIndex);
    this.options.reorder(reordered);
    this.pickedIndex.set(null);
    this.activeIndex.set(this.originalIndex);
    this.options.announce(
      SORTABLE_GRID_LABELS.cancelled(
        this.options.name(reordered[this.originalIndex]),
        this.originalIndex + 1
      )
    );
    this.options.focusItem(this.originalIndex);
  }
}
